import { useEffect, useRef, useState } from "react"
import { createPortal } from "react-dom"
import { Loader2, Database, Pencil, Trash2, X, Check, ChevronLeft, ChevronRight, Search, Filter, Download } from "lucide-react"
import AdminSidebar from "../components/AdminSidebar"
import ConfirmModal from "../components/ConfirmModal"
import { useLanguage } from "../lib/LanguageContext"

const API = process.env.REACT_APP_AUTH_API_URL || "http://localhost:8001"
const INPUT_CLASS = "rounded-lg border border-white/15 bg-ink-700/60 text-paper-0 placeholder:text-mist-500 transition-colors focus:border-gold-400/60 focus:outline-none focus:ring-1 focus:ring-gold-400/20"
const ICON_BUTTON_CLASS = "flex h-9 w-9 items-center justify-center rounded-md border border-white/20 text-mist-300 transition-colors hover:border-white/40 hover:text-paper-0 disabled:opacity-50"

function formatCell(value) {
  if (value === null || value === undefined || value === "") return <span className="text-mist-500">—</span>
  if (typeof value === "object") return <span className="font-mono text-xs">{JSON.stringify(value)}</span>
  if (typeof value === "boolean") return value ? "Yes" : "No"
  return String(value)
}

// "true"/"false" columns (paid, completed, read, connected) get Yes/No labels
// in the UI, matching how formatCell already renders them in the table body.
function optionLabel(opt) {
  if (opt === "true") return "Yes"
  if (opt === "false") return "No"
  return opt
}

// One column's Excel-style filter popover: a checkbox list for columns with a
// fixed set of options, a plain text "contains" box otherwise. Rendered via a
// portal at a fixed position — nesting it inside the table's overflow-x-auto
// wrapper would clip it, since setting overflow-x forces overflow-y to
// compute as auto too.
function ColumnFilterPopover({ anchorRect, options, value, onApply, onClose, t }) {
  const ref = useRef(null)
  const [text, setText] = useState(value || "")
  const [checked, setChecked] = useState(() => new Set((value || "").split(",").filter(Boolean)))

  useEffect(() => {
    function onPointerDown(e) {
      if (ref.current && !ref.current.contains(e.target)) onClose()
    }
    function onKeyDown(e) {
      if (e.key === "Escape") onClose()
    }
    document.addEventListener("mousedown", onPointerDown)
    document.addEventListener("keydown", onKeyDown)
    window.addEventListener("resize", onClose)
    window.addEventListener("scroll", onClose, true)
    return () => {
      document.removeEventListener("mousedown", onPointerDown)
      document.removeEventListener("keydown", onKeyDown)
      window.removeEventListener("resize", onClose)
      window.removeEventListener("scroll", onClose, true)
    }
  }, [onClose])

  const toggle = (opt) => {
    setChecked((prev) => {
      const next = new Set(prev)
      next.has(opt) ? next.delete(opt) : next.add(opt)
      return next
    })
  }

  return createPortal(
    <div
      ref={ref}
      role="dialog"
      aria-label="Column filter"
      className="fixed z-50 w-44 rounded-lg border border-white/15 bg-ink-800 p-2 normal-case tracking-normal shadow-2xl"
      style={{ top: anchorRect.bottom + 6, left: anchorRect.left, boxShadow: "0 16px 40px -8px rgba(0,0,0,.7)" }}
    >
      {options ? (
        <div className="flex flex-col gap-1.5 max-h-48 overflow-y-auto">
          {options.map((opt) => (
            <label key={opt} className="flex items-center gap-2 text-xs font-medium text-paper-0">
              <input type="checkbox" checked={checked.has(opt)} onChange={() => toggle(opt)} className="accent-gold-500" />
              {optionLabel(opt)}
            </label>
          ))}
        </div>
      ) : (
        <input
          autoFocus
          value={text}
          onChange={(e) => setText(e.target.value)}
          placeholder={t("admin_db_contains")}
          className={`h-7 w-full px-2 text-xs ${INPUT_CLASS}`}
        />
      )}
      <div className="mt-2 flex items-center justify-between gap-2">
        <button
          type="button"
          onClick={() => { onApply(""); onClose() }}
          className="rounded px-1 text-[10px] font-semibold text-mist-400 transition-colors hover:text-paper-0"
        >
          {t("admin_db_clear")}
        </button>
        <button
          type="button"
          onClick={() => { onApply(options ? [...checked].join(",") : text); onClose() }}
          className="rounded-md bg-gold-500 px-2 py-1 text-[10px] font-semibold text-ink-900 transition-colors hover:bg-gold-400"
        >
          {t("admin_db_apply")}
        </button>
      </div>
    </div>,
    document.body,
  )
}

export default function AdminDatabase() {
  const [tables, setTables] = useState([])
  const [activeTable, setActiveTable] = useState(null)
  const [tableData, setTableData] = useState(null)
  const [isLoadingTables, setIsLoadingTables] = useState(true)
  const [isLoadingRows, setIsLoadingRows] = useState(false)
  const [error, setError] = useState("")
  const { t } = useLanguage()

  const [page, setPage] = useState(1)
  const pageSize = 25
  const [search, setSearch] = useState("")
  const [searchDraft, setSearchDraft] = useState("")
  const [filters, setFilters] = useState({})
  const [openFilter, setOpenFilter] = useState(null) // { col, anchorRect } | null

  const [editingId, setEditingId] = useState(null)
  const [editDraft, setEditDraft] = useState({})
  const [saving, setSaving] = useState(false)
  const [deletingId, setDeletingId] = useState(null)
  const [deleteTarget, setDeleteTarget] = useState(null)
  const [exporting, setExporting] = useState(false)

  function authHeaders() {
    const token = localStorage.getItem("token")
    return { Authorization: `Bearer ${token}`, "Content-Type": "application/json" }
  }

  useEffect(() => {
    async function fetchTables() {
      setIsLoadingTables(true)
      try {
        const res = await fetch(`${API}/api/admin/database/tables`, { headers: authHeaders() })
        if (!res.ok) throw new Error("Failed to fetch table list")
        const json = await res.json()
        setTables(json)
        if (json.length > 0) setActiveTable(json[0].name)
      } catch (e) {
        setError(e.message)
      } finally {
        setIsLoadingTables(false)
      }
    }
    fetchTables()
  }, [])

  const fetchRows = async () => {
    if (!activeTable) return
    setIsLoadingRows(true)
    setError("")
    try {
      const params = new URLSearchParams({ page: String(page), page_size: String(pageSize), ...(search ? { q: search } : {}), ...filters })
      const res = await fetch(`${API}/api/admin/database/${activeTable}?${params}`, { headers: authHeaders() })
      if (!res.ok) throw new Error(`Failed to fetch ${activeTable}`)
      setTableData(await res.json())
    } catch (e) {
      setError(e.message)
    } finally {
      setIsLoadingRows(false)
    }
  }

  useEffect(() => { fetchRows() }, [activeTable, page, search, filters]) // eslint-disable-line react-hooks/exhaustive-deps

  const selectTable = (name) => {
    setActiveTable(name)
    setPage(1)
    setSearch("")
    setSearchDraft("")
    setFilters({})
    setEditingId(null)
  }

  const applySearch = (e) => {
    e.preventDefault()
    setPage(1)
    setSearch(searchDraft)
  }

  const applyColumnFilter = (col, value) => {
    setPage(1)
    setFilters((f) => {
      const next = { ...f }
      if (value) next[col] = value
      else delete next[col]
      return next
    })
  }

  const clearAll = () => {
    setSearchDraft("")
    setSearch("")
    setFilters({})
    setPage(1)
  }

  const startEdit = (row) => {
    setEditingId(row.id)
    setEditDraft({ ...row })
  }

  const saveEdit = async () => {
    setSaving(true)
    try {
      const res = await fetch(`${API}/api/admin/database/${activeTable}/${editingId}`, {
        method: "PUT",
        headers: authHeaders(),
        body: JSON.stringify(editDraft),
      })
      if (!res.ok) {
        const data = await res.json().catch(() => ({}))
        throw new Error(data.detail || "Failed to save changes")
      }
      setEditingId(null)
      await fetchRows()
    } catch (e) {
      setError(e.message)
    } finally {
      setSaving(false)
    }
  }

  const confirmDeleteRow = async () => {
    const id = deleteTarget
    setDeletingId(id)
    try {
      const res = await fetch(`${API}/api/admin/database/${activeTable}/${id}`, {
        method: "DELETE",
        headers: authHeaders(),
      })
      if (!res.ok) throw new Error("Failed to delete row")
      setDeleteTarget(null)
      await fetchRows()
    } catch (e) {
      setError(e.message)
      setDeleteTarget(null)
    } finally {
      setDeletingId(null)
    }
  }

  const exportCsv = async () => {
    setExporting(true)
    try {
      const params = new URLSearchParams({ ...(search ? { q: search } : {}), ...filters })
      const res = await fetch(`${API}/api/admin/database/${activeTable}/export.csv?${params}`, { headers: authHeaders() })
      if (!res.ok) throw new Error("Failed to export CSV")
      const blob = await res.blob()
      const url = URL.createObjectURL(blob)
      const a = document.createElement("a")
      a.href = url
      a.download = `${activeTable}.csv`
      a.click()
      URL.revokeObjectURL(url)
    } catch (e) {
      setError(e.message)
    } finally {
      setExporting(false)
    }
  }

  const totalPages = tableData ? Math.max(1, Math.ceil(tableData.total / pageSize)) : 1
  const hasActiveFilters = search || Object.keys(filters).length > 0

  return (
    <div className="flex flex-col min-h-screen bg-ink-900 lg:flex-row">
      <AdminSidebar />
      <main className="flex-1 overflow-y-auto p-4 sm:p-6 lg:p-10">
        <header className="mb-8">
          <h1 className="flex items-center gap-3 font-display text-3xl font-bold tracking-tight text-paper-0">
            <Database className="h-7 w-7 text-gold-400" /> {t("admin_db_title")}
          </h1>
          <p className="mt-1 text-sm text-mist-300">
            {t("admin_db_subtitle")}
          </p>
        </header>

        {error && <div role="alert" className="mb-6 rounded border border-red-500/30 bg-red-500/10 px-4 py-3 text-sm text-red-300">{error}</div>}

        {isLoadingTables ? (
          <div className="flex items-center justify-center py-20"><Loader2 className="h-8 w-8 animate-spin text-gold-400" /></div>
        ) : (
          <>
            <div className="mb-6 flex flex-wrap gap-2">
              {tables.map((t) => (
                <button
                  key={t.name}
                  onClick={() => selectTable(t.name)}
                  aria-pressed={activeTable === t.name}
                  className={`rounded-lg border px-3 py-1.5 text-xs font-semibold transition-colors ${
                    activeTable === t.name
                      ? "border-gold-500/40 bg-gold-500/10 text-gold-400"
                      : "border-white/10 bg-ink-800/30 text-mist-300 hover:bg-white/5 hover:text-paper-0"
                  }`}
                >
                  {t.name} <span className="ml-1 text-mist-500">({t.count})</span>
                </button>
              ))}
            </div>

            <form onSubmit={applySearch} className="mb-4 flex flex-wrap items-center gap-2">
              <div className="relative flex-1 min-w-[180px] max-w-xs">
                <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-mist-500" />
                <input
                  value={searchDraft}
                  onChange={(e) => setSearchDraft(e.target.value)}
                  placeholder={`${t("admin_db_search_placeholder")} ${activeTable || ""}…`}
                  aria-label={`Search ${activeTable || "table"}`}
                  className={`h-8 w-full pl-8 pr-2.5 text-xs ${INPUT_CLASS}`}
                />
              </div>
              <button type="submit" className="flex h-8 items-center gap-1.5 rounded-lg bg-gold-500 px-3 text-xs font-semibold text-ink-900 transition-colors hover:bg-gold-400">
                {t("admin_db_search_btn")}
              </button>
              {hasActiveFilters && (
                <button type="button" onClick={clearAll} className="flex h-8 items-center gap-1.5 rounded-lg border border-white/20 px-3 text-xs font-semibold text-mist-300 transition-colors hover:border-white/40">
                  <X className="h-3 w-3" /> {t("admin_db_clear_filters")}
                </button>
              )}
              <button
                type="button"
                onClick={exportCsv}
                disabled={exporting || !activeTable}
                className="ml-auto flex h-8 items-center gap-1.5 rounded-lg border border-white/20 px-3 text-xs font-semibold text-mist-300 transition-colors hover:border-white/40 disabled:cursor-not-allowed disabled:opacity-50"
              >
                {exporting ? <Loader2 className="h-3 w-3 animate-spin" /> : <Download className="h-3 w-3" />}
                {t("admin_db_export_csv")}
              </button>
            </form>

            <div className="rounded-xl border border-white/10 bg-ink-800/30 overflow-hidden shadow-sm">
              {isLoadingRows ? (
                <div className="flex items-center justify-center py-20"><Loader2 className="h-8 w-8 animate-spin text-gold-400" /></div>
              ) : tableData && tableData.rows.length > 0 ? (
                <div className="overflow-x-auto">
                  <table className="w-full text-left text-sm">
                    <thead className="border-b border-white/10 bg-ink-800/80 text-xs font-semibold uppercase tracking-wider text-mist-400">
                      <tr>
                        {tableData.columns.map((col) => {
                          const options = tableData.filter_options?.[col]
                          const filterable = col !== "id"
                          const active = !!filters[col]
                          return (
                            <th key={col} className="whitespace-nowrap px-4 py-3">
                              <span className="inline-flex items-center gap-1.5">
                                {col}
                                {filterable && (
                                  <button
                                    type="button"
                                    aria-label={`Filter ${col}`}
                                    aria-expanded={openFilter?.col === col}
                                    onClick={(e) => {
                                      const rect = e.currentTarget.getBoundingClientRect()
                                      setOpenFilter((f) => (f?.col === col ? null : { col, anchorRect: rect }))
                                    }}
                                    className={`rounded p-0.5 transition-colors focus:outline-none focus:ring-1 focus:ring-gold-400/40 ${active ? "text-gold-400" : "text-mist-500 hover:text-paper-0"}`}
                                  >
                                    <Filter className="h-3 w-3" fill={active ? "currentColor" : "none"} />
                                  </button>
                                )}
                              </span>
                              {openFilter?.col === col && (
                                <ColumnFilterPopover
                                  anchorRect={openFilter.anchorRect}
                                  options={options}
                                  value={filters[col]}
                                  onApply={(value) => applyColumnFilter(col, value)}
                                  onClose={() => setOpenFilter(null)}
                                  t={t}
                                />
                              )}
                            </th>
                          )
                        })}
                        <th className="whitespace-nowrap px-4 py-3">{t("admin_db_col_actions")}</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-white/5">
                      {tableData.rows.map((row, i) => {
                        const isEditing = editingId === row.id
                        return (
                          <tr key={row.id || i} className="hover:bg-white/[0.02]">
                            {tableData.columns.map((col) => {
                              const editOptions = tableData.filter_options?.[col]
                              return (
                                <td key={col} className="whitespace-nowrap px-4 py-3 text-mist-300">
                                  {isEditing && col !== "id" ? (
                                    editOptions ? (
                                      <select
                                        value={editDraft[col] ?? ""}
                                        onChange={(e) => setEditDraft((d) => ({ ...d, [col]: e.target.value }))}
                                        aria-label={`Edit ${col}`}
                                        className={`h-8 w-32 px-2 text-xs ${INPUT_CLASS}`}
                                      >
                                        {editOptions.map((o) => <option key={o} value={o}>{optionLabel(o)}</option>)}
                                      </select>
                                    ) : (
                                      <input
                                        value={editDraft[col] ?? ""}
                                        onChange={(e) => setEditDraft((d) => ({ ...d, [col]: e.target.value }))}
                                        aria-label={`Edit ${col}`}
                                        className={`h-8 w-32 px-2 text-xs ${INPUT_CLASS}`}
                                      />
                                    )
                                  ) : (
                                    formatCell(row[col])
                                  )}
                                </td>
                              )
                            })}
                            <td className="whitespace-nowrap px-4 py-3">
                              {isEditing ? (
                                <div className="flex items-center gap-1.5">
                                  <button
                                    onClick={saveEdit}
                                    disabled={saving}
                                    aria-label="Save changes"
                                    className="flex h-9 w-9 items-center justify-center rounded-md border border-emerald-500/30 bg-emerald-500/10 text-emerald-400 transition-colors hover:bg-emerald-500 hover:text-ink-900 disabled:opacity-50"
                                  >
                                    {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Check className="h-3.5 w-3.5" />}
                                  </button>
                                  <button onClick={() => setEditingId(null)} aria-label="Cancel edit" className={ICON_BUTTON_CLASS}>
                                    <X className="h-3.5 w-3.5" />
                                  </button>
                                </div>
                              ) : (
                                <div className="flex items-center gap-1.5">
                                  <button onClick={() => startEdit(row)} aria-label="Edit row" className={ICON_BUTTON_CLASS}>
                                    <Pencil className="h-3.5 w-3.5" />
                                  </button>
                                  <button
                                    onClick={() => setDeleteTarget(row.id)}
                                    disabled={deletingId === row.id}
                                    aria-label="Delete row"
                                    className="flex h-9 w-9 items-center justify-center rounded-md border border-red-500/30 bg-red-500/10 text-red-400 transition-colors hover:bg-red-500 hover:text-white disabled:opacity-50"
                                  >
                                    {deletingId === row.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Trash2 className="h-3.5 w-3.5" />}
                                  </button>
                                </div>
                              )}
                            </td>
                          </tr>
                        )
                      })}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="py-12 text-center text-sm text-mist-500">
                  {hasActiveFilters ? (
                    <>
                      <p>{t("admin_db_empty_filtered")}</p>
                      <button type="button" onClick={clearAll} className="mt-2 font-semibold text-gold-400 transition-colors hover:text-gold-300">
                        {t("admin_db_clear_filters")}
                      </button>
                    </>
                  ) : (
                    <p>{t("admin_db_empty_table")}</p>
                  )}
                </div>
              )}
            </div>

            {tableData && tableData.total > 0 && (
              <div className="mt-4 flex flex-wrap items-center justify-between gap-3 text-sm text-mist-400">
                <p>
                  {t("admin_db_showing")} {(page - 1) * pageSize + 1}–{Math.min(page * pageSize, tableData.total)} {t("admin_db_of")} {tableData.total}
                </p>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                    disabled={page <= 1}
                    aria-label="Previous page"
                    className="flex h-9 w-9 items-center justify-center rounded-md border border-white/20 transition-colors hover:border-white/40 disabled:cursor-not-allowed disabled:opacity-40"
                  >
                    <ChevronLeft className="h-4 w-4" />
                  </button>
                  <span className="text-xs font-semibold text-paper-0">{t("admin_db_page")} {page} / {totalPages}</span>
                  <button
                    onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                    disabled={page >= totalPages}
                    aria-label="Next page"
                    className="flex h-9 w-9 items-center justify-center rounded-md border border-white/20 transition-colors hover:border-white/40 disabled:cursor-not-allowed disabled:opacity-40"
                  >
                    <ChevronRight className="h-4 w-4" />
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </main>

      <ConfirmModal
        open={!!deleteTarget}
        title={t("admin_db_delete_title")}
        message={t("admin_db_delete_msg")}
        confirmLabel={t("admin_db_delete_btn")}
        busy={!!deletingId}
        onClose={() => setDeleteTarget(null)}
        onConfirm={confirmDeleteRow}
      />
    </div>
  )
}
